import { type Descendant, type Editor as SlateEditor, type Operation } from 'slate';

import { htmlToSlate } from './deserialize';
import { getEditorHtmlContent, getEditorTextContent } from './queries';
import { focusEditor } from './selection';
import { type SerializeTextOptions, slateToText } from './serialize';
import {
  applyEditorTextChanges,
  replaceSlateEditorHtml,
  replaceSlateEditorText,
} from './transforms';
import type { EditorTextChange } from './types';

export const hasSlateContentChangeOperation = (operations: Operation[]): boolean =>
  operations.some((op) => 'set_selection' !== op.type);

export const hasSlateSelectionOperation = (operations: Operation[]): boolean =>
  operations.some((op) => 'set_selection' === op.type);

export const createEditorInstanceImperativeHandle = (editor: SlateEditor) => {
  return {
    applyTextChanges: (changes: EditorTextChange[]): void => {
      applyEditorTextChanges(editor, changes);
    },
    getEditor: (): SlateEditor => {
      return editor;
    },
    getValue: (): Descendant[] => {
      return editor.children;
    },
    getText: (): string => {
      return getEditorTextContent(editor);
    },
    getHtml: (): string => {
      return getEditorHtmlContent(editor);
    },
    replaceHtml: (newHtml: string): void => {
      replaceSlateEditorHtml(editor, newHtml);
    },
    replaceText: (newText: string): void => {
      replaceSlateEditorText(editor, newText);
    },
    focus: (atEnd = true): void => {
      if (editor) {
        focusEditor(editor, { atEnd });
      }
    },
  };
};

/**
 * Convert HTML content to plain text
 * @param htmlContent - HTML content
 * @param [options] - text serialization options
 * @returns - text representation of the HTML content
 */
export const convertHtmlContentToPlainText = (
  htmlContent: string,
  options?: SerializeTextOptions
): string => {
  const slateValue = htmlToSlate(htmlContent);
  return slateToText(slateValue, options);
};
